import { get, has, isFunction, isNil } from 'lodash';
import { isObservable, Observable } from 'rxjs';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChange,
  ViewEncapsulation,
} from '@angular/core';
import { asObservable, getIcon } from '@shared/utils';
import { ICON_TYPE, IIconOptions } from '@shared/types';

@Component({
  selector: 'xux-expand-collapse-icon',
  templateUrl: 'xux-expand-collapse-icon.component.html',
  styleUrls: ['xux-expand-collapse-icon.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class XuxExpandCollapseIconComponent implements OnInit, OnChanges {
  @Input() public collapsed: boolean;

  @Input() public iconConfig: IIconOptions = {};

  @Output() public onCollapsed: EventEmitter<boolean> =
    new EventEmitter<boolean>();

  public get RTL(): boolean {
    return get(this.iconConfig, 'RTL', false);
  }

  public get showCaption(): boolean {
    return get(this.iconConfig, 'showCaption', false);
  }

  public get caption(): Observable<string> {
    const configCaption = get(this.iconConfig, 'caption', '');

    return (
      isObservable(configCaption) ? configCaption : asObservable(configCaption)
    ) as Observable<string>;
  }

  public get imageTypeGetter(): IIconOptions['imageTypeGetter'] {
    return get(this.iconConfig, 'imageTypeGetter', null);
  }

  public get typeAsImageName(): boolean {
    return isFunction(this.imageTypeGetter);
  }

  public iconPath: string;
  public isCollapsed: boolean;

  public getIconType(): ICON_TYPE {
    if (isFunction(this.imageTypeGetter)) {
      return this.imageTypeGetter(this.isCollapsed, {
        config: this.iconConfig,
      });
    }

    if (this.RTL) {
      return this.isCollapsed
        ? ICON_TYPE.DOUBLE_ARROW_LEFT
        : ICON_TYPE.DOUBLE_ARROW_RIGHT;
    } else {
      return this.isCollapsed
        ? ICON_TYPE.DOUBLE_ARROW_RIGHT
        : ICON_TYPE.DOUBLE_ARROW_LEFT;
    }
  }

  public ngOnInit(): void {
    this.isCollapsed = isNil(this.collapsed) ? true : this.collapsed;
    this.iconPath = getIcon(this.getIconType(), {
      typeAsImageName: this.typeAsImageName,
    });
  }

  public ngOnChanges(
    changes: Record<keyof XuxExpandCollapseIconComponent, SimpleChange>,
  ): void {
    if (has(changes, 'collapsed')) {
      this.isCollapsed = isNil(this.collapsed) ? true : this.collapsed;
      this.iconPath = getIcon(this.getIconType(), {
        typeAsImageName: this.typeAsImageName,
      });
    }
  }

  /**
   * Toggles the expanded collapse state
   */
  public onSwitchIcon(): void {
    this.isCollapsed = !this.isCollapsed;
    this.iconPath = getIcon(this.getIconType(), {
      typeAsImageName: this.typeAsImageName,
    });
    this.onCollapsed.emit(this.isCollapsed);
  }
}
